import { FunctionComponent, useContext, useMemo } from 'react';

import {
  Button,
  Label,
  Separator,
  PercentIcon,
  BonusIcon,
  WalletIcon,
  InfoBlock,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { useIsFeatureAvailable } from 'features';
import { useSetDiscount, useSetDeposit, useSetBonus } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EDiscountsType } from 'types/user';
import { getClientRoleLabel } from 'utils';

import { ClientsAvatar } from '../../ClientsAvatar';
import { EditableField } from './EditableField';
import s from './Info.module.scss';
import { IClientCardProps } from './interfaces';

export const Info: FunctionComponent<IClientCardProps> = ({
  client,
  close,
  setIsDepositOpen,
  setIsDefaultClient,
  notShowButtonDeposit,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency, isBonusSystemEnabled },
    },
  } = useContext(SettingsContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const isManage = useMemo<boolean>(
    () => permissions?.has('client_manage') || false,
    [permissions]
  );

  const isBonusCashAvailable = useIsFeatureAvailable('smart_bonus');

  const setDeposit = useSetDeposit(t);

  const setDiscount = useSetDiscount(t);

  const setBonus = useSetBonus(t);

  const { displayedDiscount, displayedDeposit, displayBonus } = useMemo(() => {
    return {
      displayedDeposit: client.deposit
        ? `${client.deposit.toLocaleString([], {
            maximumFractionDigits: 2,
          })} ${currency.sign}`
        : t('clients.deposit.placeholder'),
      displayBonus: client.bonus
        ? String(client.bonus)
        : t('clients.bonuses.placeholder'),
      displayedDiscount: client.user_discount
        ? `${client.user_discount}%`
        : t('clients.discount.placeholder'),
    };
  }, [client]);

  const group = client.group;

  const ClientRoleLabel = getClientRoleLabel(client.roles ?? [], t);

  return (
    <>
      <div className={s.container}>
        <div className={s.header}>
          <ClientsAvatar avatar={client?.avatar_url} roles={client?.roles} />
          <div className={s.header_info}>
            <span className={s.login}>
              {client.nickname ?? client.phone}
              {close && (
                <Button
                  variant="ghost"
                  onClick={close}
                  className={s.close}
                  iconName="CloseIcon"
                />
              )}
            </span>
            {client.phone}
          </div>
        </div>
        {client.banned_at && (
          <InfoBlock
            color="red"
            iconName="LockIcon"
            title={t('clientCard.banned.title')}
            className={s.banned}
          />
        )}
        {!notShowButtonDeposit && permissions?.has('payment_create') && (
          <Button
            fullWidth
            className={s.button}
            iconName="WalletIcon"
            color="blue"
            onClick={(event) => {
              if (close) {
                close(event);
              }
              setIsDefaultClient && setIsDefaultClient(client?.uuid);
              setTimeout(() => {
                setIsDepositOpen && setIsDepositOpen(true);
              }, 0);
            }}
            disabled={
              !!client.banned_at ||
              client.roles?.some((role) => role.alias === 'active_operator')
            }
          >
            {t('deposit.fill')}
          </Button>
        )}

        <div className={s.labels}>
          {client.unverified && (
            <Label
              text={t('client.status.unverified')}
              color="orange"
              iconName="AlertIcon"
            />
          )}
          {ClientRoleLabel}
          {client.roles?.some((role) => role.alias === 'active_operator') && (
            <Label text={t('workShifts.status.onShift')} color="violet" />
          )}
          {group && (
            <Label
              text={`${group.title} ${group.discount}%`}
              color="gray-primary"
            />
          )}
          {client.discounts?.find(
            (disc) => disc.type === EDiscountsType.NETWORK
          )?.value && (
            <Tooltip
              content={t(
                'max.discount.network',
                client.discounts?.find(
                  (disc) => disc.type === EDiscountsType.NETWORK
                )?.value ?? 0
              )}
              contentClassName={s.discount_tooltip}
            >
              <Label color="green" iconName="PercentIcon" />
            </Tooltip>
          )}
        </div>
        <Separator />
        <div className={cn(s.row, isManage && s['manager-view'])}>
          <WalletIcon size="m" color="link-info" className={s.icon} />
          {isManage ? (
            <EditableField
              defaultValue={client.deposit ?? 0}
              sign={currency.sign}
              onSave={(value) => {
                if (!client.uuid) return Promise.reject();

                return setDeposit({
                  variables: {
                    input: { client_uuid: client.uuid, value: value || 0 },
                  },
                });
              }}
              validate={(value) => {
                if (value === '') return t('errors.required');
                if (value < 0) return t('errors.min', 0, currency.sign);

                if (value > 16000000) {
                  return t('errors.max', '16 000 000', currency.sign);
                }
              }}
            />
          ) : (
            displayedDeposit
          )}
        </div>
        <div className={cn(s.row, isManage && s['manager-view'])}>
          <BonusIcon size="m" color="link-info" className={s.icon} />
          {isManage ? (
            <EditableField
              defaultValue={client.bonus ?? 0}
              onSave={(value) => {
                if (!client.uuid) return Promise.reject();

                return setBonus({
                  variables: {
                    input: { client_uuid: client.uuid, value: value || 0 },
                  },
                });
              }}
              validate={(value) => {
                if (value === '') return t('errors.required');
                if (value < 0) return t('errors.min', 0);

                if (value > 16000000) {
                  return t('errors.max', '16 000 000');
                }
              }}
              disabled={!isBonusCashAvailable || !isBonusSystemEnabled}
            />
          ) : (
            displayBonus
          )}
        </div>
        <div className={cn(s.row, isManage && s['manager-view'])}>
          <PercentIcon size="m" color="link-info" className={s.icon} />
          {isManage ? (
            <EditableField
              defaultValue={
                client.discounts?.find(
                  (disc) => disc.type === EDiscountsType.PERSONAL
                )?.value ?? 0
              }
              sign="%"
              onSave={(value) => {
                if (!client.uuid) return Promise.reject();

                return setDiscount({
                  variables: {
                    input: { client_uuid: client.uuid, value: value || 0 },
                  },
                });
              }}
              validate={(value) => {
                if (value === '') return t('errors.required');
                if (value < 0) return t('errors.min', '0%');
                if (value > 100) return t('errors.max', '100%');
              }}
            />
          ) : (
            displayedDiscount
          )}
        </div>
      </div>
    </>
  );
};
